// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — ieEconomy.js   ⟡ THE CANONICAL ENGINE FILE ⟡
// ────────────────────────────────────────────────────────────────────
// ⚠ KEEP BYTE-IDENTICAL IN EVERY IE REPO. One copy per app's src/,
//   all of them moved the same day. The master lives at
//   gamemaster/engine/ieEconomy.js — edit THAT, then re-copy.
//
// ── WHY THIS FILE EXISTS (2026-08-13, THE GREAT RECALIBRATION) ──────
// `ie-cross-game-economy` has named this file as canon since 2026-08-06:
// "the implementation, in every repo, byte-identical, read it before
// writing any money code." IT DID NOT EXIST. Not in one repo, not
// anywhere in mass_transfer. Sol Survivor flagged that in a comment
// rather than reimplementing it behind Pierre's back, and was right to.
//
// The cost of it not existing was not theoretical. Pierre's Crest Well
// filters every picker through a hardcoded `CURRENCIES` const holding
// exactly THREE rows — DC, CC, SE. So ⋔ Kindling, ❦ Blood Money and
// ❋ Dewdrops, all three of them shipped, earnable, and named in the
// skill as REGISTERED, could not appear at the Well at all. A rate row
// alone was never enough, because the code that reads rates threw away
// every id the const did not already know.
//
// This file is the fix. THE RATE ROW IS THE REGISTRY, actually:
//   • `registerCurrency()` writes one row. Zero deploys.
//   • The Well builds its picker from the ROWS, not from a const.
//   • `KNOWN_CURRENCIES` below is a display fallback for a row that
//     has not loaded yet — never a gate. A currency missing from it
//     still trades.
//
// ── WHAT IT CARRIES ─────────────────────────────────────────────────
//   THE TWO-CURRENCY LAW ·  THE LANE LAW  ·  THE TOIL LAW
//   THE FAUCET LAW       ·  delta-never-read-modify-write
//   grantCrests()        ·  the registry  ·  the clamp constants
// ════════════════════════════════════════════════════════════════════

import {
  ref, get, onValue, push, update, runTransaction, serverTimestamp,
} from "firebase/database";

// Every repo has a src/firebase.js exporting `db`. That is the ONE
// import that differs by nothing but path, and it is always this path.
import { db } from "./firebase";

export const IE_ECONOMY_VERSION = "2.1";

// ════════════════════════════════════════════════════════════════════
// §1 · WHAT A DIVINE CREST IS WORTH   (settled 2026-08-13)
// ────────────────────────────────────────────────────────────────────
// Every rate in the Well was set before a single thing could be bought
// with a Crest. Lyraea is the first storefront, and it prices a track
// at ◆1 — which forced the question the Empire had been deferring:
// is a Crest precious, or is it change?
//
//   ⟡ ONE DIVINE CREST = ONE QUARTER OF A FOCUSED HOUR OF PLAY. ⟡
//
// That is the unit. It is not a new number — it is the Empire
// reference of 3–5 ◆/hour, stated from the other end, where it can
// actually be used. Read it in both directions:
//
//   • A player who sits down for an hour in ANY title leaves with
//     about four Crests. That is the Toil Law's whole promise.
//   • A ◆1 track in Lyraea costs fifteen minutes of play. Meaningful,
//     not punishing — you can hear a song on your lunch break.
//   • A ◆5 Lord achievement is worth an hour and a quarter. It should
//     feel like it.
//
// ⚠ THE CONSEQUENCE NOBODY LIKES: a Crest is now PRICED. Every rate is
//   checkable against it, and any title whose measured earn rate does
//   not divide to ≈4 ◆/hour has a wrong rate — not a taste difference.
//   That is the point. An unpriced reserve cannot be audited.
//
// The arithmetic, once, so nobody re-derives it wrong:
//     perCrest = (currency a competent player earns in one focused hour) / 4
// ════════════════════════════════════════════════════════════════════

export const CREST_MINUTES = 15;              // one ◆ ≈ 15 minutes of play
export const CRESTS_PER_HOUR = 60 / CREST_MINUTES;   // = 4

// Derive a rate from a measured earn rate. Use this instead of guessing.
//   perCrestFor(4000)  → 1000   (Snake Eyes, the measured baseline)
//   perCrestFor(48)    → 12     (Sol Survivor)
export const perCrestFor = (earnedPerFocusedHour) =>
  Math.max(1, Math.round((Number(earnedPerFocusedHour) || 0) / CRESTS_PER_HOUR));

// The inverse, for a sanity check on a rate somebody already set.
export const impliedHourlyFor = (perCrest) =>
  (Number(perCrest) || 0) * CRESTS_PER_HOUR;

// Is an existing rate consistent with the doctrine, given a measured
// hourly? Tolerance is deliberately wide — ±40% — because "roughly the
// same sweat" is the law, not "identically the same sweat". A rate
// outside this band is a bug; a rate inside it is a taste difference
// and nobody should be retuned over it.
export const rateAgreesWithDoctrine = (perCrest, measuredHourly) => {
  const want = perCrestFor(measuredHourly);
  if (!want || !perCrest) return false;
  const ratio = perCrest / want;
  return ratio >= 0.6 && ratio <= 1.4;
};

// ════════════════════════════════════════════════════════════════════
// §2 · THE REGISTRY
// ────────────────────────────────────────────────────────────────────
// A currency is a ROW at pierre/exchange/rates/{currencyId}. The row is
// the registration. There is no second list that must agree with it.
//
// A row:
//   currencyId  "SE"                     permanent, uppercase, short
//   name        "Snake Eyes Coins"       display
//   short       "SE"
//   icon        "◈"                      HUD fallback; real art on the CDN
//   owningApp   "snake-eyes"             THE LANE LAW — only this app writes it
//   path        "snakeEyesCoins"         → profiles/{uid}/{path}
//   fullPath    optional; overrides path entirely. "{uid}" is resolved
//               at read time. Derive it from your path constant — NEVER
//               type a Firebase path twice.
//   perCrest    1000                     THE TOIL LAW
//   measuredAt  "2026-07-25" | null      when the earn rate was measured
//   provisional true                     "this is a guess, re-check me"
//   active      true                     false → invisible everywhere
// ════════════════════════════════════════════════════════════════════

// ⟡ ONE BASE PATH CONSTANT. Every Pierre-owned path derives from it.
//
// ── MOVED 2026-08-13 (THE GREAT RECALIBRATION 2/3) ──────────────────
// These two constants used to read `pierre/exchange/rates` and
// `pierre/mint` — a TOP-LEVEL node that DOES NOT EXIST in the ruleset
// and never has. Every read and every write against them fell straight
// through to the `$other: { ".read": false, ".write": false }` floor at
// the bottom of rules.json. Verified live, 2026-08-13: both denied.
//
// The consequences were silent and total, since the day the Well
// shipped:
//   • The mint log has never recorded a single grant. `grantCrests()`
//     pushed into a denied node and swallowed it.
//   • The rate registry has never been readable OR writable by any
//     client, so `registerCurrency()` and `ensureRateTable()` were
//     always no-ops and the Well always saw an empty table.
//   • `pierre/players` — the Pier's own presence node — was denied too.
//
// `games/pierre` is covered by the live `games/$gameId` umbrella, so
// this works the moment the client deploys, with ZERO rules changes,
// and it obeys the law the old path broke: NEVER A NEW TOP-LEVEL NODE.
// Nothing was migrated because nothing was ever written.
export const PIERRE_BASE   = "games/pierre";
export const RATES_PATH    = `${PIERRE_BASE}/exchange/rates`;
export const MINT_PATH     = `${PIERRE_BASE}/mint`;
export const COOLDOWN_PATH = `${PIERRE_BASE}/exchange/cooldown`;
export const HISTORY_PATH  = `${PIERRE_BASE}/exchange/history`;
export const PLAYERS_PATH  = `${PIERRE_BASE}/players`;

export const DC_ID      = "DC";

// Divine Crests are NOT a row. The reserve has no rate to itself, is
// owned by nobody, and cannot be deactivated.
export const CRESTS = {
  currencyId: "DC",
  name: "Divine Crests",
  short: "DC",
  icon: "◆",
  owningApp: null,
  path: "divineCrests",
};

// ── DISPLAY FALLBACK, NOT A GATE ────────────────────────────────────
// If the rate table is still loading, or a brand-new title registered
// itself ten seconds ago, these give a picker something to render. A
// currency absent from this map still trades perfectly — the row wins
// over this block on every field. Adding a title here is a courtesy,
// not a step.
export const KNOWN_CURRENCIES = {
  DC: { ...CRESTS, tint: "#f0d060" },
  CC: { currencyId: "CC", name: "Chocolate Coins",  short: "CC", icon: "◕", owningApp: "terror",       path: "chocolateCoins", tint: "#c98b4b" },
  SE: { currencyId: "SE", name: "Snake Eyes Coins", short: "SE", icon: "◈", owningApp: "snake-eyes",   path: "snakeEyesCoins", tint: "#22d3ee" },
  KD: { currencyId: "KD", name: "Kindling",         short: "KD", icon: "⋔", owningApp: "perry",        path: "kindling",       tint: "#e0763a" },
  BM: { currencyId: "BM", name: "Lyf Force",        short: "LF", icon: "❦", owningApp: "annihilation", path: "annihilation___bloodMoney", tint: "#a83b3b" },
  DW: { currencyId: "DW", name: "Dewdrops",         short: "DW", icon: "❋", owningApp: "bebecitos",    path: null, fullPath: "games/bebecitos/gardens/{uid}/dewdrops", tint: "#7fb98a" },
  SOL:{ currencyId: "SOL",name: "Sol",              short: "SOL",icon: "♥", owningApp: "sol-survivor", path: "sol",            tint: "#e8b84b" },
  SW: { currencyId: "SW", name: "Crumbs",           short: "CRB",icon: "⌁", owningApp: "swat",         path: "crumbs",         tint: "#c9b48b" },
};

// ⚠ ANNIHILATION'S PATH IS LANED. Its balance is at
//   profiles/{uid}/annihilation___bloodMoney, not .../bloodMoney. A
//   Well that reads the unlaned path finds 0 and tells the player they
//   are broke while they are looking at a full purse. The row above is
//   the only place that path is written down outside Annihilation.

export const currencyMeta = (id, rates) =>
  (rates && rates[id]) ||
  KNOWN_CURRENCIES[id] ||
  { currencyId: id, name: id, short: id, icon: "•", path: null };

export const tintFor = (id, rates) =>
  (rates && rates[id]?.tint) || KNOWN_CURRENCIES[id]?.tint || "#9aa4ad";

// Where a currency's balance actually lives for a given player.
// fullPath wins; otherwise profiles/{uid}/{path}. Returns null when
// neither is known, and callers must treat null as "cannot read",
// never as "zero" — a silent zero is how a player gets told they are
// broke by a bug.
export const balancePath = (uid, meta) => {
  if (!uid || !meta) return null;
  if (meta.fullPath) return String(meta.fullPath).replace("{uid}", uid);
  if (meta.path) return `profiles/${uid}/${meta.path}`;
  return null;
};

// ── REGISTER ────────────────────────────────────────────────────────
// One write. Zero deploys. The Well picks it up live, in every title,
// immediately. Overwrites the row — use this when you MEAN to retune.
export async function registerCurrency(row) {
  const id = row?.currencyId;
  if (!id) throw new Error("[IE/ECONOMY] registerCurrency needs a currencyId.");
  if (id === DC_ID) throw new Error("[IE/ECONOMY] DC is the reserve. It has no rate row.");
  const perCrest = Number(row.perCrest);
  if (!Number.isFinite(perCrest) || perCrest <= 0) {
    throw new Error(`[IE/ECONOMY] ${id}: perCrest must be a positive number.`);
  }
  const clean = {
    currencyId: id,
    name: row.name || id,
    short: row.short || id,
    icon: row.icon || "•",
    owningApp: row.owningApp || null,
    path: row.path || null,
    perCrest,
    active: row.active !== false,
    provisional: row.provisional !== false,
    measuredAt: row.measuredAt || null,
    note: row.note || null,
  };
  if (row.fullPath) clean.fullPath = row.fullPath;
  if (row.tint) clean.tint = row.tint;
  await update(ref(db, `${RATES_PATH}/${id}`), clean);
  return clean;
}

// ── ENSURE ──────────────────────────────────────────────────────────
// Safe on a boot path. Seeds the row once and is inert forever after.
// ⚠ IT WILL NOT OVERWRITE A RATE caP'n HAS TUNED IN THE CONSOLE. That
//   is the entire difference between this and registerCurrency, and it
//   is why boot code calls this one. A boot path that overwrites live
//   tuning un-tunes the Empire every time anybody opens the app.
export async function ensureCurrency(row) {
  const id = row?.currencyId;
  if (!id || id === DC_ID) return "skipped";
  const tx = await runTransaction(ref(db, `${RATES_PATH}/${id}`), (cur) =>
    cur ? undefined : { ...row, currencyId: id, active: row.active !== false }
  );
  return tx.committed ? "seeded" : "present";
}

const _normalizeRates = (raw) => {
  const out = {};
  for (const [id, row] of Object.entries(raw || {})) {
    if (!row || row.active === false) continue;
    const perCrest = Number(row.perCrest);
    if (!Number.isFinite(perCrest) || perCrest <= 0) continue;
    // The row wins, but a missing display field falls back to the map
    // so a half-filled row never renders as `undefined`.
    out[id] = { ...(KNOWN_CURRENCIES[id] || {}), ...row, currencyId: id, perCrest };
  }
  return out;
};

export const listenToRates = (callback) =>
  onValue(ref(db, RATES_PATH), (snap) => callback(_normalizeRates(snap.val())));

export const getRates = async () => {
  const snap = await get(ref(db, RATES_PATH));
  return _normalizeRates(snap.val());
};

// ════════════════════════════════════════════════════════════════════
// §3 · READING BALANCES  —  read anyone's
// ════════════════════════════════════════════════════════════════════

export const listenToCurrency = (uid, currencyId, callback, rates) => {
  if (currencyId === DC_ID) {
    if (!uid) { callback(0); return () => {}; }
    return onValue(ref(db, `profiles/${uid}/divineCrests`), (s) =>
      callback(s.exists() ? (s.val() || 0) : 0));
  }
  const p = balancePath(uid, currencyMeta(currencyId, rates));
  if (!p) { callback(0); return () => {}; }
  return onValue(ref(db, p), (s) => callback(s.exists() ? (s.val() || 0) : 0));
};

// Live map of every currency the player holds, built from the ROWS —
// which is the whole point. A title registered five minutes ago shows
// up in this wallet with no client deploy anywhere.
export const listenToWallet = (uid, callback) => {
  if (!uid) { callback({}); return () => {}; }
  const wallet = {};
  let leafUnsubs = [];
  const stopLeaves = () => {
    leafUnsubs.forEach((u) => { if (typeof u === "function") u(); });
    leafUnsubs = [];
  };
  const stopRates = listenToRates((rates) => {
    stopLeaves();
    const ids = [DC_ID, ...Object.keys(rates)];
    leafUnsubs = ids.map((id) =>
      listenToCurrency(uid, id, (v) => {
        wallet[id] = v;
        callback({ ...wallet });
      }, rates)
    );
  });
  return () => { stopLeaves(); if (typeof stopRates === "function") stopRates(); };
};

// ════════════════════════════════════════════════════════════════════
// §4 · WRITING  —  write only your own
// ────────────────────────────────────────────────────────────────────
// DELTA, NEVER READ-MODIFY-WRITE. Add 5. Do not read 600 and write
// 605. The leaf is the single number at the end of the path, never the
// whole profile object — that shape caused the April 2026 holo-card
// and Crest wipe, when two writes landed at once and one silently
// overwrote the other. Add-only cannot do that.
// ════════════════════════════════════════════════════════════════════

export async function deltaCurrency(uid, currencyId, delta, { owningApp, rates } = {}) {
  if (!uid || !Number.isFinite(delta) || delta === 0) return null;
  const meta = currencyMeta(currencyId, rates);

  // THE LANE LAW, enforced in code as a guardrail. The real seal is
  // Firebase rules; this just turns a silent cross-title write into a
  // console line pointing at the call site that did it.
  if (currencyId !== DC_ID && owningApp && meta.owningApp && owningApp !== meta.owningApp) {
    // eslint-disable-next-line no-console
    console.warn(
      `[IE/ECONOMY] Lane Law: ${owningApp} may not write ${currencyId} (owned by ${meta.owningApp}). Write only your own; read anyone's.`
    );
    return null;
  }

  const p = currencyId === DC_ID
    ? `profiles/${uid}/divineCrests`
    : balancePath(uid, meta);
  if (!p) {
    // eslint-disable-next-line no-console
    console.warn(`[IE/ECONOMY] No known path for ${currencyId}. Nothing written.`);
    return null;
  }

  const amt = Math.floor(delta);
  // ⚠ THE EMPTY-CACHE REFUSAL (2026-09-16): a first run can see null
  // because the node isn't loaded. A DEBIT hands that null back so the
  // handler re-runs on the server's value; `applied` is decided per run,
  // so a no-op commit (the node truly absent = 0) is reported as refused.
  let applied = false;
  const tx = await runTransaction(ref(db, p), (cur) => {
    applied = false;
    if ((cur === null || cur === undefined) && amt < 0) return cur;
    const bal = cur || 0;
    const next = bal + amt;
    if (next < 0) return undefined;   // abort — never goes negative
    applied = true;
    return next;
  });
  return tx.committed && applied ? (tx.snapshot.val() || 0) : null;
}

// ════════════════════════════════════════════════════════════════════
// §5 · grantCrests()  —  THE SHARED ◆ HELPER
// ────────────────────────────────────────────────────────────────────
// ⚠⚠ THE MOST IMPORTANT FUNCTION IN THE EMPIRE. ⚠⚠
// DC is granted from MANY apps and must be granted ONE WAY. Every
// title imports this. NOBODY reimplements it.
//
// LEGAL FAUCETS:   achievements · boss first-kills · secrets · the Well
// ILLEGAL FAUCETS: score conversion · daily login · anything repeatable
//
// sourceKey names the FAUCET, not the app — "terror:gatekeeper_first",
// never "terror". When something inflates you want to know which door.
//
// GRANT BEFORE FLAG. Grant first, set the claimed flag second. A crash
// between them costs a duplicate grant, which is recoverable. The
// reverse costs a player their reward, which is not.
// ════════════════════════════════════════════════════════════════════

// ⚠ THE CLAMP CEILING. The Layer One rules clamp refuses any single
//   ◆ write that jumps more than this. Code must stay BELOW the rule
//   or a legitimate payout fails in production and looks like a
//   Firebase bug — that is the code-before-rules trap wearing a hat.
//   Keep these two numbers in this order, always:
//        GRANT_CEILING  <  CLAMP_MAX_DELTA  (in rules.json)
export const CLAMP_MAX_DELTA = 5000;   // the rule's number. Mirror only.
export const GRANT_CEILING   = 2500;   // the code's number. Half of it.

// ⚠ A LARGE GRANT IS CHUNKED, NEVER TRUNCATED.
//   The obvious implementation of a ceiling is `amt = min(amt, ceiling)`
//   — and it is quiet theft. The Well is a legal faucet, and a player
//   converting a genuinely large pile of a soft currency is entitled to
//   every Crest of it; capping that write would take the difference and
//   tell them nothing. So an over-ceiling grant is paid out as several
//   under-ceiling writes instead. Each one passes the rules clamp on its
//   own, the total is exact, and the mint log records ONE entry for the
//   real amount so the audit trail still reads as the single event it
//   was.
export async function grantCrests(uid, amount, sourceKey = "unknown") {
  if (!uid || !Number.isFinite(amount) || amount <= 0) return 0;
  const amt = Math.floor(amount);
  const leaf = ref(db, `profiles/${uid}/divineCrests`);

  // 1 · GRANT — delta on the leaf, add-only, in clamp-sized bites.
  let paid = 0;
  while (paid < amt) {
    const bite = Math.min(GRANT_CEILING, amt - paid);
    // ⚠⚠ `runTransaction` RESOLVES `{ committed: false }` INSTEAD OF
    // THROWING when the update aborts or retries run out. `paid += bite`
    // ran regardless, so a refused bite produced a mint record and a
    // return value for Crests that were never credited — the audit
    // trail saying money exists that does not. `grantDawgs` in
    // upDawgs.js has always broken the ladder on a refusal; this is that
    // pattern, which is the one to copy.
    // Found by adversarial review 2026-09-14.
    // eslint-disable-next-line no-await-in-loop
    const tx = await runTransaction(leaf, (cur) => (cur || 0) + bite);
    if (!tx?.committed) break;     // whatever landed stays landed, honestly
    paid += bite;
  }
  if (paid <= 0) return 0;

  // 2 · PROVENANCE — append-only. Who minted, when, why. Never rewritten.
  // ⚠⚠ THIS APPEND MUST NEVER REJECT, because by the time it runs the
  // Crests are ALREADY IN THE ACCOUNT. An uncaught rejection here made
  // `grantCrests` throw after a committed credit — and the Well's caller
  // reads a throw as "nothing landed" and issues a FULL refund, so the
  // player kept the Crests and got the offering back. A missing audit
  // row is worth strictly less than a minted Crest, so it loses.
  // Found by adversarial review of the fix itself, 2026-09-14.
  await push(ref(db, `${MINT_PATH}/${uid}`), {
    // ⚠ THE LEDGER RECORDS WHAT LANDED. `amount: amt` recorded what was
    // asked for, so a short ladder wrote a mint row for Crests that are
    // not in the account — and a mint log that disagrees with the
    // balance is worse than no mint log.
    amount: paid,
    requested: amt,
    sourceKey: String(sourceKey).slice(0, 64),
    at: serverTimestamp(),
    ...(paid > GRANT_CEILING ? { writes: Math.ceil(paid / GRANT_CEILING) } : {}),
  }).catch(() => { /* the money landed; the row is best-effort */ });

  // ⚠ WHAT LANDED, NOT WHAT WAS ASKED FOR. The mint log was fixed to
  // record `paid` and this line still returned `amt`, which is the only
  // number any caller can see — so a Well conversion INTO ◆ that credited
  // one bite of five still reported the full output on the receipt and
  // refunded nothing. Same asymmetry, one line further down.
  return paid;
}

// Spend ◆. Aborts if they cannot afford it; returns true only on a
// committed debit. Spending is never blocked by the clamp — a decrease
// is self-harm, not an exploit.
export async function spendCrests(uid, amount) {
  if (!uid || !Number.isFinite(amount) || amount <= 0) return false;
  const amt = Math.floor(amount);
  // ⚠⚠ THE EMPTY-CACHE REFUSAL. A transaction handler's FIRST run can see
  // `null` because the client has not loaded the node yet — not because
  // the balance is zero. Aborting there (`bal < amt` → undefined) refused
  // a player who could afford it, with "Not enough Divine Crests". The
  // standard answer is to hand `null` straight back: the write conflicts
  // with the server's real value and the handler runs again on it.
  // `charged` is decided per run, so a commit of that no-op null (the
  // node genuinely does not exist = zero Crests) reports NOT paid.
  // Found by adversarial review of King's Grace, 2026-09-16.
  let charged = false;
  const tx = await runTransaction(ref(db, `profiles/${uid}/divineCrests`), (cur) => {
    charged = false;
    if (cur === null || cur === undefined) return cur;
    if (cur < amt) return undefined;
    charged = true;
    return cur - amt;
  });
  return !!(tx && tx.committed && charged);
}

// ── THE LARGEST EXCHANGE THAT HAS EVER LANDED ───────────────────────
// The clamp cap is meant to be set from evidence, not from a feeling.
// This is that query. Run it once from any authed console; the number
// it returns is the floor for CLAMP_MAX_DELTA.
export async function largestMintEver() {
  const snap = await get(ref(db, MINT_PATH));
  let max = 0, at = null, sourceKey = null, uid = null;
  snap.forEach((player) => {
    player.forEach((entry) => {
      const v = Number(entry.val()?.amount) || 0;
      if (v > max) { max = v; at = entry.val()?.at; sourceKey = entry.val()?.sourceKey; uid = player.key; }
    });
  });
  return { max, at, sourceKey, uid };
}

// ════════════════════════════════════════════════════════════════════
// §6 · THE FAUCET LAW, as a check you can actually run
// ────────────────────────────────────────────────────────────────────
// A ◆ source that can fire twice is illegal without caP'n's personal
// sign-off. There is no numeric cap because the bound is STRUCTURAL —
// a door that only opens once needs no ceiling.
// ════════════════════════════════════════════════════════════════════

export const LEGAL_FAUCET_KINDS = ["achievement", "first_kill", "secret", "exchange"];

export function isFaucetLegal(faucet) {
  if (!faucet) return false;
  if (faucet.repeatable) return false;
  return LEGAL_FAUCET_KINDS.includes(faucet.kind);
}

// ════════════════════════════════════════════════════════════════════
// §7 · THE TWO-CURRENCY LAW, as an assertion
// ────────────────────────────────────────────────────────────────────
// Call this once at boot with your title's earnable numbers. If a
// third one ever appears — an XP pool, a Renown pool, skill points,
// shards, tokens — this is where it surfaces, at the moment somebody
// adds it, instead of a year later during a migration.
//
// A scoreboard is NOT a currency: if it cannot be spent, it is not in
// the count and it does not belong in profiles/.
// ════════════════════════════════════════════════════════════════════

export function assertTwoCurrencies(title, earnableNumbers = []) {
  if (earnableNumbers.length === 2) return true;
  // eslint-disable-next-line no-console
  console.error(
    `[IE/ECONOMY] ${title}: two earnable numbers — yours and ◆. Found ${earnableNumbers.length}: ${earnableNumbers.join(", ")}. If one of these can only ever be spent on one kind of thing, fold it into the other.`
  );
  return false;
}

export default {
  IE_ECONOMY_VERSION, CRESTS, KNOWN_CURRENCIES, DC_ID,
  registerCurrency, ensureCurrency, listenToRates, getRates,
  listenToCurrency, listenToWallet, deltaCurrency,
  grantCrests, spendCrests, perCrestFor, impliedHourlyFor,
};
